import React from 'react';

import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  IconButton,
  Box,
  Typography,
} from '@mui/material';



const fixedWidth = (width: number) => ({

  width,

  minWidth: width,

  maxWidth: width,

  boxSizing: 'border-box',

});



type HeaderCell = [string, number];



interface ActionButton<T extends object> {

  icon: React.ReactNode;

  onClick: (row: T) => void;

  condition?: (row: T) => boolean;

}



interface ListTableProps<T extends object> {

  headerCells: HeaderCell[];

  rows: T[];

  isLoading?: boolean;

  noDataText?: string;

  actions?: ActionButton<T>[];

  renderCell?: (row: T, columnIndex: number) => React.ReactNode;

}



// ColGroup ensures perfect width alignment for header & body

const ColGroup = ({ headerCells }: { headerCells: [string, number][] }) => (

  <colgroup>

    {headerCells.map(([_, width], i) => (

      <col

        key={i}

        style={{

          width: `${width}px`,

          minWidth: `${width}px`,

          maxWidth: `${width}px`,

        }}

      />

    ))}

  </colgroup>

);



export const ListTable = <T extends object>({

  headerCells,

  rows,

  isLoading = false,

  noDataText = 'No data found',

  actions,

  renderCell,

}: ListTableProps<T>) => {

  return (

    <TableContainer

      sx={{

        overflow: 'hidden',

        height: '100%',

        minHeight: 0,

        display: 'flex',

        flexDirection: 'column',

        borderTopRightRadius: 10,

        borderTopLeftRadius: 10,

      }}

    >

      <Box sx={{ flexShrink: 0 }}>

        <Table

          sx={{

            tableLayout: 'fixed',

            width: '100%',

            borderCollapse: 'separate',

            borderSpacing: '0 5px',

          }}

        >

          <ColGroup headerCells={headerCells} />

          <TableHead>

            <TableRow>

              {headerCells.map(([label, width], index) => (

                <TableCell

                  key={index}

                  sx={{

                    textAlign: 'center',

                    color: '#FFFFFF',

                    backgroundColor: '#1C1C1C',

                    ...fixedWidth(width),

                    padding: '25px 0px',

                    borderTop: '1px solid #8A8D91',

                    borderBottom: '1px solid #8A8D91',

                    borderLeft: index === 0 ? '1px solid #8A8D91' : 'none',

                    borderRight:

                      index === headerCells.length - 1

                        ? '1px solid #8A8D91'

                        : 'none',

                    borderTopLeftRadius: index === 0 ? 10 : 0,

                    borderBottomLeftRadius: index === 0 ? 10 : 0,

                    borderTopRightRadius:

                      index === headerCells.length - 1 ? 10 : 0,

                    borderBottomRightRadius:

                      index === headerCells.length - 1 ? 10 : 0,

                  }}

                >

                  {label === 'Action' && actions && actions.length > 0

                    ? 'Action'

                    : label}

                </TableCell>

              ))}

            </TableRow>

          </TableHead>

        </Table>

      </Box>



      <Box

        data-scroll-body

        sx={{

          flex: '1 1 auto',

          minHeight: 0,

          overflowY: 'auto',

          scrollbarWidth: 'none',

        }}

      >

        <Table

          sx={{

            tableLayout: 'fixed',

            width: '100%',

            borderCollapse: 'separate',

            borderSpacing: '0 8px',

          }}

        >

          <ColGroup headerCells={headerCells} />

          <TableBody>

            {isLoading ? (

              <TableRow>

                <TableCell colSpan={headerCells.length} align="center">

                  Loading...

                </TableCell>

              </TableRow>

            ) : rows.length === 0 ? (

              <TableRow>

                <TableCell colSpan={headerCells.length} align="center">

                  {noDataText}

                </TableCell>

              </TableRow>

            ) : (

              rows.map((row, rowIndex) => (

                <TableRow

                  key={rowIndex}

                  sx={{

                    backgroundColor: '#1C1C1C',



                    '& > td': {

                      borderTop: '1px solid #3E3E3E',

                      borderBottom: '1px solid #3E3E3E',

                      color: '#8A8D91',

                      paddingY: '10px',

                      textAlign: 'center',

                      backgroundColor: '#1C1C1C',

                    },



                    '& > td:first-of-type': {

                      borderLeft: '1px solid #3E3E3E',

                      borderTopLeftRadius: 12,

                      borderBottomLeftRadius: 12,

                    },



                    '& > td:last-of-type': {

                      borderRight: '1px solid #3E3E3E',

                      borderTopRightRadius: 12,

                      borderBottomRightRadius: 12,

                    },

                  }}

                >

                  {headerCells.map(([label, width], colIdx) => (

                    <TableCell

                      key={colIdx}

                      sx={{

                        ...fixedWidth(width),

                        textAlign: 'center',

                      }}

                    >

                      {label === 'Action' && actions && actions.length > 0 ? (
                        (() => {
                          const visibleActions = actions.filter(
                            (action) =>
                              !action.condition || action.condition(row),
                          );

                          if (visibleActions.length === 0) {
                            return (
                              <Typography variant="body2" color="#888">
                                -
                              </Typography>
                            );
                          }

                          return (
                            <Box
                              sx={{
                                display: 'flex',
                                justifyContent: 'center',
                                gap: 0,
                              }}
                            >
                              {visibleActions.map((action, i) => (
                                <IconButton
                                  key={i}
                                  sx={{ color: '#E31E25' }}
                                  onClick={() => action.onClick(row)}
                                >
                                  {action.icon}
                                </IconButton>
                              ))}
                            </Box>
                          );
                        })()
                      ) : renderCell ? (

                        renderCell(row, colIdx)

                      ) : (

                        String(row[Object.keys(row)[colIdx] as keyof T] ?? '')

                      )}

                    </TableCell>

                  ))}

                </TableRow>

              ))

            )}

          </TableBody>

        </Table>

      </Box>

    </TableContainer>

  );

};


